// Placeholder text for template variables
// Shows realistic preview data in blue to indicate it's a variable

export const VARIABLE_PLACEHOLDERS = {
  // Customer variables
  'customer.name': "John Smith",
  'customer.email': "john.smith@example.com",
  'customer.phone': "(555) 123-4567",

  // Appointment variables
  'appointment.date': "January 15, 2025",
  'appointment.time': "2:30 PM",
  'appointment.service_address': "123 Maple Street, Austin TX",
  'appointment.service_type': "Weekly Pool Cleaning",

  // Business variables
  'business.name': "Acme Pool Service",
  'business.phone': "(555) 987-6543",
  'business.email': "info@acmepools.com",
  'business.initials': "APS",

  // Invoice variables
  'invoice.invoice_number': "INV-0042",
  'invoice.date': "January 15, 2025",
  'invoice.due_date': "February 15, 2025",
  'invoice.total_amount': "$250.00",
  'invoice.subtotal': "$225.00",
  'invoice.tax_amount': "$25.00",
  'invoice.payment_url': "pay.acmepools.com/inv/abc123"
};

/**
 * Get placeholder text for a variable key
 * @param {string} variableKey - The variable key (e.g., 'customer.name')
 * @returns {string} - Placeholder text or the key if not found
 */
export const getPlaceholderForVariable = (variableKey) => {
  return VARIABLE_PLACEHOLDERS[variableKey] || variableKey;
};

/**
 * Convert email editor pill HTML to {{variable}} syntax
 * @param {string} html - HTML containing variable pill spans
 * @returns {string} - Text with {{variables}}
 */
export const convertEmailPillsToBrackets = (html) => {
  if (!html) return html;

  // Match the email pill format: <span data-variable-key="..." ...>...</span>
  const result = html.replace(/<span[^>]*data-variable-key="([^"]+)"[^>]*>.*?<\/span>/gs, '{{$1}}');

  return result;
};

/**
 * Convert {{variable}} syntax to styled placeholder HTML
 * @param {string} text - Text containing {{variables}}
 * @returns {string} - HTML with styled placeholder spans
 */
export const convertVariablesToPlaceholders = (text) => {
  if (!text) return text;

  const result = text.replace(/\{\{([^}]+)\}\}/g, (match, variableKey) => {
    const trimmedKey = variableKey.trim();
    const placeholder = getPlaceholderForVariable(trimmedKey);

    // Inline styles to ensure blue color with dotted underline appears in email builder canvas
    const inlineStyles = 'color: #3b82f6; font-weight: 500; text-decoration: underline; text-decoration-style: dotted; text-decoration-color: rgba(59, 130, 246, 0.4); text-underline-offset: 2px;';

    return `<span class="variable-placeholder" data-variable-key="${trimmedKey}" style="${inlineStyles}">${placeholder}</span>`;
  });

  return result;
};

/**
 * Convert {{variable}} syntax to plain placeholder text (for plain text contexts)
 * @param {string} text - Text containing {{variables}}
 * @returns {string} - Plain text with placeholders
 */
export const convertVariablesToPlaceholderText = (text) => {
  if (!text) return text;

  const result = text.replace(/\{\{([^}]+)\}\}/g, (match, variableKey) => {
    const trimmedKey = variableKey.trim();
    const placeholder = getPlaceholderForVariable(trimmedKey);
    return placeholder;
  });

  return result;
};

/**
 * Convert placeholder HTML back to {{variable}} syntax
 * @param {string} html - HTML containing placeholder spans
 * @returns {string} - Text with {{variables}}
 */
export const convertPlaceholdersToVariables = (html) => {
  if (!html) return html;

  return html.replace(/<span[^>]*class="variable-placeholder"[^>]*data-variable-key="([^"]+)"[^>]*>([^<]*)<\/span>/g, '{{$1}}');
};
